/**
 * The catalogue cover is a row column (`cover_path`), not a content block: uploaded with
 * `cover: true`, never part of `data` or `data.media`. Glue only — the upload itself belongs to
 * `AssignmentImageUploader`.
 */
import { computed, watch } from 'vue'

import type { ComputedRef } from 'vue'

import { useAssignmentEditorStore } from './store'
import { rememberAssignmentMediaUrl } from './media-asset.hook'
import { isAssignmentMediaPath } from '../../lib/assignments/assignment-media'

export interface UseAssignmentCoverUploaderReturn {
  /** Object path, never a signed URL. */
  refCoverPath: ComputedRef<string>
  refHasCover: ComputedRef<boolean>
  /** Where the cover does NOT appear, in the words of the template's own content. */
  refHint: ComputedRef<string>
  setCover: (value: string) => void
}

export const useAssignmentCoverUploader = (): UseAssignmentCoverUploaderReturn => {
  const store = useAssignmentEditorStore()

  const refCoverPath = computed<string>(() => store.getCoverPath ?? '')
  const refHasCover = computed<boolean>(() => store.hasCover)

  const refHint = computed<string>(() => (store.isMath
    ? 'Catalogue art. It is not part of any exercise and never appears inside one.'
    : 'Catalogue art. It is not one of the book\'s chapters and never appears inside a page.'))

  const setCover = (value: string): void => {
    const next = value.trim()

    // `null` clears the column; `''` would be a valid path to nothing.
    store.setCoverPath(next === '' ? null : next)
    // The previous signed read describes the previous object.
    store.setCoverUrl(null)
  }

  // Seed the signed read from the response so the thumbnail needs no second round trip.
  watch(
    [() => store.getCoverPath, () => store.getCoverUrl, () => store.getMediaExpiresAt],
    ([path, url, expiresAt]) => {
      if (!path || !url || !isAssignmentMediaPath(path)) {
        return
      }
      rememberAssignmentMediaUrl(path, url, expiresAt)
    },
    { immediate: true },
  )

  return {
    refCoverPath,
    refHasCover,
    refHint,
    setCover,
  }
}
